// Advisory duplicate hint (Story #4613, Stream 8a). A debounced, ≥8-char search
// of the requester's OWN tickets (from the user-index list the My-tickets tab
// already loads) surfaces likely duplicates. It is advisory only: dismissible,
// and it NEVER blocks submit.

import { useEffect, useMemo, useState } from 'react'
import { Lightbulb, X } from 'lucide-react'
import { DUPLICATE_HINT_MIN_CHARS } from '../../constants/support'
import type { SupportTicket } from '../../types/support'

function tokens(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 3)
}

function matchTickets(subject: string, tickets: SupportTicket[]): SupportTicket[] {
  const wanted = new Set(tokens(subject))
  if (wanted.size === 0) return []
  return tickets
    .filter((t) => t.status !== 'closed')
    .map((t) => {
      const have = new Set(tokens(t.subject))
      let overlap = 0
      wanted.forEach((w) => {
        if (have.has(w)) overlap += 1
      })
      return { t, overlap }
    })
    .filter((x) => x.overlap >= 2)
    .sort((a, b) => b.overlap - a.overlap)
    .slice(0, 3)
    .map((x) => x.t)
}

interface DuplicateHintProps {
  subject: string
  tickets: SupportTicket[]
  onOpenTicket: (ticketId: string) => void
}

export default function DuplicateHint({ subject, tickets, onOpenTicket }: DuplicateHintProps) {
  const [debounced, setDebounced] = useState('')
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(subject.trim()), 400)
    return () => clearTimeout(id)
  }, [subject])

  // A materially different subject re-arms the (previously dismissed) hint.
  useEffect(() => {
    setDismissed(false)
  }, [debounced])

  const matches = useMemo(
    () => (debounced.length >= DUPLICATE_HINT_MIN_CHARS ? matchTickets(debounced, tickets) : []),
    [debounced, tickets],
  )

  if (dismissed || matches.length === 0) return null

  return (
    <div className="mt-2 rounded-lg border border-iflow-200 bg-iflow-50 px-3 py-2 text-[12.5px] text-iflow-900">
      <div className="flex items-start gap-2">
        <Lightbulb size={15} className="mt-0.5 shrink-0 text-iflow-600" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="font-medium">You already have a similar open ticket — want to add to it instead?</p>
          <ul className="mt-1 space-y-0.5">
            {matches.map((t) => (
              <li key={t.ticket_id}>
                <button
                  type="button"
                  onClick={() => onOpenTicket(t.ticket_id)}
                  className="text-left text-iflow-700 hover:underline"
                >
                  <span className="font-semibold">{t.ref}</span> — {t.subject}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="shrink-0 rounded p-0.5 text-iflow-400 hover:bg-iflow-100 hover:text-iflow-700"
          aria-label="Dismiss duplicate suggestion"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  )
}
